import { useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion"
import { cn } from "../lib/utils"

/**
 * Standalone accordion-item renderer.
 *
 * When accordion-item appears as a child of an accordion, the AccordionRenderer
 * handles it inline. This renderer handles the case where accordion-item appears
 * as a standalone element type (e.g., in blocks-v2 that use it outside accordion
 * or when the element tree is flat).
 */
export function AccordionItemRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()

  const handleValueChange = useCallback((value: string | string[]) => {
    const isOpen = Array.isArray(value) ? value.length > 0 : !!value
    dispatchEvent(element.id, "toggle", { open: isOpen })
  }, [element.id, dispatchEvent])

  const title = element.text
    ? String(resolveSymbol(element.text) ?? "")
    : "Section"

  return (
    <Accordion multiple onValueChange={handleValueChange}>
      <AccordionItem value={element.id} className={cn(element.className)}>
        <AccordionTrigger>
          {title}
        </AccordionTrigger>
        <AccordionContent>
          {element.children?.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
